import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getPageHeader } from '@/constants'
import { Button } from '@/components/ui/button'
import { ListBrowseToolbar } from '@/components/filters/list-browse-toolbar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import { useThreads, Thread } from '@/hooks/use-threads'
import { useThreadSearch } from '@/hooks/use-thread-search'
import { authorizedFetch } from '@/lib/api'
import { useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Trash2 } from 'lucide-react'
import { ThreadSearchResultCard } from './thread-search-result'
import { SearchLoadingState } from './search-loading-state'
import { ThreadsEmptyState, ThreadsListSkeleton, ThreadCard } from './threads-page-list'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'

export interface ThreadsPageProps {
  chatFocusRoute: string
  onOpenInAgent: (threadId: string) => void
}

export function ThreadsPage({ chatFocusRoute, onOpenInAgent }: ThreadsPageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const currentThreadParam = searchParams.get('thread')
  const queryClient = useQueryClient()
  const { threads, isLoading, error } = useThreads()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [previousDebouncedTerm, setPreviousDebouncedTerm] = useState('')
  const [wasSearching, setWasSearching] = useState(false)
  const [queryChangedFlag, setQueryChangedFlag] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const [deleteThreadId, setDeleteThreadId] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const itemsPerPage = 20

  useEffect(() => {
    if (renamingThreadId) renameInputRef.current?.focus()
  }, [renamingThreadId])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const { results: searchResults, total: searchTotal, isLoading: isSearching } = useThreadSearch(
    debouncedSearchTerm,
    debouncedSearchTerm.trim().length > 0,
  )

  useEffect(() => {
    if (isSearching) {
      setWasSearching(true)
    } else if (!isSearching && wasSearching) {
      const timer = setTimeout(() => setWasSearching(false), 100)
      return () => clearTimeout(timer)
    }
  }, [isSearching, wasSearching])

  useEffect(() => {
    if (debouncedSearchTerm !== previousDebouncedTerm && previousDebouncedTerm !== '') {
      if (wasSearching) {
        setQueryChangedFlag(true)
        const timer = setTimeout(() => setQueryChangedFlag(false), 2000)
        return () => clearTimeout(timer)
      }
    }
    if (debouncedSearchTerm !== previousDebouncedTerm) {
      setPreviousDebouncedTerm(debouncedSearchTerm)
    }
  }, [debouncedSearchTerm, previousDebouncedTerm, wasSearching])

  const isPreparing = searchTerm !== debouncedSearchTerm && searchTerm.trim().length > 0
  const queryChangedMidSearch = queryChangedFlag

  const sortedThreads = threads
  const singleHumanMessageThreads = sortedThreads.filter((t) => t.message_count === 1)
  const singleHumanMessageCount = singleHumanMessageThreads.length

  const totalPages = Math.ceil(sortedThreads.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedThreads = sortedThreads.slice(startIndex, startIndex + itemsPerPage)

  const handleThreadClick = (threadId: string) => {
    navigateWithThreadPreservation(navigate, `/threads/${threadId}`, searchParams)
  }

  const startRename = (thread: Thread) => {
    setRenamingThreadId(thread.id)
    setRenameDraft(thread.subject || '')
  }

  const commitRename = useCallback(
    async (thread: Thread) => {
      if (renamingThreadId !== thread.id) return
      const trimmed = renameDraft.trim()
      if (!trimmed || trimmed === (thread.subject || '')) {
        setRenamingThreadId(null)
        return
      }

      setIsUpdating(true)
      try {
        const response = await authorizedFetch(`/api/threads/${thread.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject: trimmed }),
        })
        if (!response.ok) throw new Error('Failed to update thread title')
        await queryClient.invalidateQueries({ queryKey: ['threads'] })
        await queryClient.invalidateQueries({ queryKey: ['thread', thread.id] })
        setRenamingThreadId(null)
      } catch (err) {
        console.error('Failed to update thread title:', err)
      } finally {
        setIsUpdating(false)
      }
    },
    [renamingThreadId, renameDraft, queryClient],
  )

  const handleConfirmDelete = async () => {
    if (!deleteThreadId) return
    setIsDeleting(true)
    try {
      const response = await authorizedFetch(`/api/threads/${deleteThreadId}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Failed to delete thread')
      await queryClient.invalidateQueries({ queryKey: ['threads'] })
      setDeleteThreadId(null)
    } catch (err) {
      console.error('Failed to delete thread:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true)
    try {
      const response = await authorizedFetch('/api/threads/bulk/delete-single-human', {
        method: 'DELETE',
      })
      if (!response.ok) throw new Error('Failed to bulk delete threads')
      await queryClient.invalidateQueries({ queryKey: ['threads'] })
      setShowBulkDeleteDialog(false)
    } catch (err) {
      console.error('Failed to bulk delete threads:', err)
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const { title, description } = getPageHeader('/threads', 'Threads', 'Your conversation threads')

  return (
    <PageContainer>
        <PageHeaderStack>
          <PageHeader
            leading={<PageHeaderLeading />}
            title={title}
            description={description}
            icon={<MessageSquare className="h-5 w-5" />}
          />

          <ListBrowseToolbar
            searchId="threads-search"
            searchPlaceholder="Search threads…"
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </PageHeaderStack>

        {!isLoading && !error && singleHumanMessageCount > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">
              {singleHumanMessageCount}{' '}
              {singleHumanMessageCount === 1 ? 'thread contains' : 'threads contain'} only a single
              human message.
            </span>
            <Button
              variant="outlineDestructive"
              size="sm"
              onClick={() => setShowBulkDeleteDialog(true)}
              disabled={isBulkDeleting}
            >
              <Trash2 className="h-4 w-4" />
              Delete short threads
            </Button>
          </div>
        ) : null}

        {isLoading ? (
          <ThreadsListSkeleton />
        ) : error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-destructive">
            Failed to load threads: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : debouncedSearchTerm.trim().length > 0 ? (
          isPreparing || isSearching || queryChangedMidSearch ? (
            <SearchLoadingState
              searchTerm={searchTerm}
              debouncedSearchTerm={debouncedSearchTerm}
              isPreparing={isPreparing}
              isSearching={isSearching}
              queryChanged={queryChangedMidSearch}
            />
          ) : searchResults.length > 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Found {searchTotal} result{searchTotal !== 1 ? 's' : ''} for &ldquo;{debouncedSearchTerm}&rdquo;
              </div>
              <PaneResponsiveCardGrid>
                {searchResults.map((result) => (
                  <ThreadSearchResultCard
                    key={result.id}
                    result={result}
                    onClick={handleThreadClick}
                  />
                ))}
              </PaneResponsiveCardGrid>
            </div>
          ) : (
            <ThreadsEmptyState
              title="No threads found"
              description={`No threads match your search query "${debouncedSearchTerm}".`}
            />
          )
        ) : paginatedThreads.length === 0 ? (
          <ThreadsEmptyState
            title="No threads yet"
            description="Start a conversation to create your first thread."
            ctaLabel="Start a conversation"
            onCta={() => navigate(chatFocusRoute)}
          />
        ) : (
          <>
            {!isLoading && !error ? (
              <p className="text-sm text-muted-foreground">
                {threads.length} {threads.length === 1 ? 'thread' : 'threads'}
              </p>
            ) : null}
            <PaneResponsiveCardGrid>
              {paginatedThreads.map((thread) => (
                <ThreadCard
                  key={thread.id}
                  thread={thread}
                  isAlreadyInChat={currentThreadParam === thread.id}
                  isRenaming={renamingThreadId === thread.id}
                  renameDraft={renameDraft}
                  isUpdating={isUpdating}
                  setRenameDraft={setRenameDraft}
                  startRename={startRename}
                  cancelRename={() => setRenamingThreadId(null)}
                  commitRename={commitRename}
                  onOpen={() => handleThreadClick(thread.id)}
                  onOpenInChat={() => onOpenInAgent(thread.id)}
                  onDelete={() => setDeleteThreadId(thread.id)}
                />
              ))}
            </PaneResponsiveCardGrid>

            {totalPages > 1 ? (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}–
                  {Math.min(startIndex + itemsPerPage, sortedThreads.length)} of{' '}
                  {sortedThreads.length}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-2 text-sm tabular-nums">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}

      <AlertDialog
        open={!!deleteThreadId}
        onOpenChange={(open) => !open && setDeleteThreadId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete thread</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this thread? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showBulkDeleteDialog}
        onOpenChange={(open) => !open && setShowBulkDeleteDialog(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete short threads</DialogTitle>
            <DialogDescription>
              This will delete <strong>{singleHumanMessageCount}</strong> thread
              {singleHumanMessageCount === 1 ? '' : 's'} containing only a single human message.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteDialog(false)}
              disabled={isBulkDeleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting}>
              {isBulkDeleting
                ? 'Deleting…'
                : `Delete ${singleHumanMessageCount} thread${singleHumanMessageCount === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
